import { useMemo } from "react";
import { TableBody, TableRow } from "@mui/material";
import { Link as RouterLink } from "react-router";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { useDismissOrderUpdateMutation } from "features/api/orderApi.ts";
import { OrderUpdate } from "types/api/orders/getOrderUpdates.ts";
import {
	ActionItemActions,
	ActionItemDescription,
	ActionItemDismissLink,
	ActionItemGoButton,
	ActionItemWarningIcon
} from "../ActionItemsPage.styles.ts";
import * as Text from "../ActionItemsPage.text.ts";
import {
	ActionItemGoTarget,
	formatActionItemDate,
	formatActionItemDescription,
	formatActionType,
	getActionItemDateSortValue,
	getActionItemGoTarget,
	getActionItemOrderId
} from "../utils/ActionItemsPage.utils.ts";

interface ActionItemsTableProps {
	actionItems: OrderUpdate[];
}

type ActionItemsSortableColumn = "description" | "actionType" | "assignedAt";

interface ActionItemRowModel {
	rowId: string;
	orderId?: string;
	goTarget?: ActionItemGoTarget;
	description: string;
	actionType: string;
	assignedAt: number;
	assignedAtDisplay: string;
}

const buildActionItemRowModel = (actionItem: OrderUpdate, index: number): ActionItemRowModel => ({
	rowId: `${actionItem.orderNumber ?? "order"}-${actionItem.statusReason ?? "reason"}-${index}`,
	orderId: getActionItemOrderId(actionItem),
	goTarget: getActionItemGoTarget(actionItem),
	description: formatActionItemDescription(actionItem),
	actionType: formatActionType(actionItem.statusReason),
	assignedAt: getActionItemDateSortValue(actionItem.updatedAt),
	assignedAtDisplay: formatActionItemDate(actionItem.updatedAt)
});

interface ActionItemsTableRowProps extends ActionItemRowModel {
	isDismissing: boolean;
	onDismiss: (orderId: string) => void;
}

const ActionItemGoControl = ({ goTarget }: { goTarget: ActionItemGoTarget }) => {
	if (goTarget.kind === "internal") {
		return (
			<ActionItemGoButton
				variant="outlined"
				color="primary"
				size="small"
				component={RouterLink}
				to={goTarget.href}
			>
				{Text.GoButtonText}
			</ActionItemGoButton>
		);
	}

	return (
		<ActionItemGoButton
			variant="outlined"
			color="primary"
			size="small"
			href={goTarget.href}
		>
			{Text.GoButtonText}
		</ActionItemGoButton>
	);
};

const ActionItemsTableRow = ({
	rowId,
	orderId,
	goTarget,
	description,
	actionType,
	assignedAtDisplay,
	isDismissing,
	onDismiss
}: ActionItemsTableRowProps) => (
	<TableRow data-testid={`action-item-${rowId}`}>
		<StyledTableCell data-testid="action-item-description">
			<ActionItemDescription>
				<ActionItemWarningIcon
					fontSize="small"
					data-testid="action-item-warning-icon"
				/>
				<span>{description}</span>
			</ActionItemDescription>
		</StyledTableCell>
		<StyledTableCell data-testid="action-item-action-type">{actionType}</StyledTableCell>
		<StyledTableCell data-testid="action-item-date-assigned">{assignedAtDisplay}</StyledTableCell>
		<StyledTableCell
			data-testid="action-item-actions"
			fixedWidth="180px"
		>
			{(goTarget || orderId) && (
				<ActionItemActions>
					{goTarget && <ActionItemGoControl goTarget={goTarget} />}
					{orderId && (
						<ActionItemDismissLink
							component="button"
							type="button"
							disabled={isDismissing}
							onClick={() => onDismiss(orderId)}
						>
							{Text.DismissLinkText}
						</ActionItemDismissLink>
					)}
				</ActionItemActions>
			)}
		</StyledTableCell>
	</TableRow>
);

export const ActionItemsTable = ({ actionItems }: ActionItemsTableProps) => {
	const [dismissOrderUpdate, { isLoading, originalArgs }] = useDismissOrderUpdateMutation();
	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort<ActionItemsSortableColumn>(
		"assignedAt",
		"desc"
	);

	const sortedActionItems = useMemo(
		() => actionItems.map(buildActionItemRowModel).sort(getComparator(order, orderBy)),
		[actionItems, getComparator, order, orderBy]
	);

	// Note: avoid the id "updatedAt" here, SortableTableHead renders a "Date Last Saved" tooltip for it
	const headCells = useMemo(
		() =>
			[
				{ id: "description", label: Text.DescriptionColumnText, sortable: true },
				{ id: "actionType", label: Text.ActionTypeColumnText, sortable: true },
				{ id: "assignedAt", label: Text.DateAssignedColumnText, sortable: true },
				{ id: "actions", label: "", sortable: false }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false
			})) satisfies HeadCell[],
		[]
	);

	return (
		<StyledTableWrapper
			data-testid="action-items-table"
			aria-label="Action Items Table"
		>
			<SortableTableHead
				orderBy={orderBy}
				order={order}
				onRequestSort={handleRequestSort}
				headCells={headCells}
			/>
			<TableBody>
				{sortedActionItems.map((actionItem) => (
					<ActionItemsTableRow
						key={actionItem.rowId}
						{...actionItem}
						isDismissing={isLoading && originalArgs === actionItem.orderId}
						onDismiss={(orderId) => {
							void dismissOrderUpdate(orderId);
						}}
					/>
				))}
			</TableBody>
		</StyledTableWrapper>
	);
};
